import { Api } from './api'
import {
    EditPasswordDocument,
    EditProfileDocument,
    ForgotPasswordDocument,
    GetCurrentPrincipalDocument,
    GetCurrentProfileDocument,
    LoginDocument,
    type LoginResponse,
    type PrincipalFragment,
    type ProfileAttribute,
    type ProfileFragment,
    type ProfileInput,
    SignUpDocument,
    VerifySignUpDocument,
} from '~/lib/graphql/graphql'
import type { NetworkClient } from '~/lib/bosca/networkclient'
import type { AsyncData } from "nuxt/app";

export interface User {
    id: string
    principal: string
    name: string
    email: string
    anonymous: boolean
    attributes: ProfileAttribute[]
}

export class Security<T extends NetworkClient> extends Api<T> {
    // eslint-disable-next-line
	constructor(network: T) {
        super(network)
    }

    async getCurrentPrincipal(): Promise<PrincipalFragment> {
        const response = await this.network.execute(GetCurrentPrincipalDocument)
        return response?.security?.principal as PrincipalFragment || {
            __typename: 'Principal',
            id: '00000000-0000-0000-0000-000000000000',
            groups: [],
            credentials: [],
            verified: false,
        }
    }

    getCurrentUserAsyncData(): AsyncData<User | null, any> {
        return this.executeAndTransformAsyncData(
            GetCurrentProfileDocument,
            {},
            (response) => {
                if (!response?.profiles?.current) {
                    return {
                        id: '00000000-0000-0000-0000-000000000000',
                        principal: '00000000-0000-0000-0000-000000000000',
                        name: '',
                        email: '',
                        anonymous: true,
                        attributes: []
                    } as User
                }
                const profile = response?.profiles?.current as ProfileFragment
                const name = profile.profileAttributes.find(a => a.typeId === 'bosca.profiles.name')?.attributes?.name || ''
                const email = profile.profileAttributes.find(a => a.typeId === 'bosca.profiles.email')?.attributes?.email || ''
                return {
                    id: profile.id,
                    principal: profile.principal?.id || '00000000-0000-0000-0000-000000000000',
                    name: name,
                    email: email,
                    anonymous: (profile.id === '00000000-0000-0000-0000-000000000000'),
                    attributes: profile.profileAttributes
                } as User
            },
            {
                authenticate: true
            }
        )
    }

    async getCurrentProfile(): Promise<ProfileFragment> {
        const response = await this.network.execute(GetCurrentProfileDocument)
        return (response?.profiles?.current as ProfileFragment | null) || ({
            __typename: 'Profile',
            id: '00000000-0000-0000-0000-000000000000',
            principal: {
                id: '00000000-0000-0000-0000-000000000000',
                verified: false
            },
            profileAttributes: []
        } as unknown as ProfileFragment)
    }

    async loginWithPassword(
        identifier: string,
        password: string,
    ): Promise<LoginResponse> {
        const response = await this.network.execute(LoginDocument, {
            identifier,
            password,
        })
        if (!response) throw new Error('invalid response')
        // @ts-expect-error: not sure why this is confused
        const login = response.security.login.password
        this.network.token = login.token.token
        return login
    }

    async logout() {
        this.network.token = null
    }

    async verify(token: string): Promise<void> {
        await this.network.execute(VerifySignUpDocument, {
            token,
        })
    }

    async signUpWithPassword(
        profile: ProfileInput,
        identifier: string,
        password: string,
    ) {
        await this.network.execute(SignUpDocument, {
            profile,
            identifier,
            password,
        })
    }

    async editPassword(
        identifier: string | null,
        oldPassword: string,
        newPassword: string,
    ) {
        await this.network.execute(EditPasswordDocument, {
            identifier,
            oldPassword,
            newPassword,
        })
    }

    async forgotPassword(identifier: string | null): Promise<boolean> {
        return (await this.network.execute(ForgotPasswordDocument, { identifier }))?.security?.login?.forgotPassword || false
    }

    async editProfile(id: string, profile: ProfileInput) {
        await this.network.execute(EditProfileDocument, {
            id, profile
        })
    }
}
